import type { IWidget, WidgetSourceType } from '@/interfaces/components/dashboard/widget'
import type { IIndicatorSourceData } from '@/interfaces/api/widget'

export const GROUP_INDICATOR_FIELDS = [
  'utilization_percentage',
  'total_consumption_kwh',
  'peak_consumption_kwh',
  'off_peak_consumption_kwh',
  'peak_demand_max',
  'off_peak_demand_max',
  'total_utilization_time',
  'total_non_utilization_time',
] as const

export type GroupIndicatorField = (typeof GROUP_INDICATOR_FIELDS)[number]

export function isGroupWidget(widget: IWidget): boolean {
  if (widget.config.source_type === 'group') return true
  return Array.isArray(widget.config.group_ids) && widget.config.group_ids.some(Boolean)
}

export function getWidgetSourceType(widget: IWidget): WidgetSourceType {
  return isGroupWidget(widget) ? 'group' : 'sensor'
}

export function getWidgetSensorId(widget: IWidget): string | null {
  const id = widget.config.sensor_ids?.[0]
  return id ? String(id) : null
}

export function getWidgetGroupId(widget: IWidget): string | null {
  const id = widget.config.group_ids?.[0]
  return id ? String(id) : null
}

export function indicatorSourceKeyFromParts(
  sourceType: WidgetSourceType,
  sourceId: string | number,
  dataField: string,
): string {
  return `${sourceType}:${sourceId}:${dataField}`
}

export function indicatorSourceKeyFromResult(result: IIndicatorSourceData): string | null {
  const sourceType = result.source_type ?? (result.group_id != null ? 'group' : 'sensor')
  if (sourceType === 'group') {
    if (result.group_id == null) return null
    return indicatorSourceKeyFromParts('group', result.group_id, result.data_field)
  }
  if (result.sensor_id == null) return null
  return indicatorSourceKeyFromParts('sensor', result.sensor_id, result.data_field)
}

export function indicatorSourceKeyFromWidget(widget: IWidget, dataField: string): string | null {
  if (!dataField) return null
  if (isGroupWidget(widget)) {
    const groupId = getWidgetGroupId(widget)
    if (!groupId) return null
    return indicatorSourceKeyFromParts('group', groupId, dataField)
  }
  const sensorId = getWidgetSensorId(widget)
  if (!sensorId) return null
  return indicatorSourceKeyFromParts('sensor', sensorId, dataField)
}
